/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";

import { DeviceModel } from "../../../model/device";
import type { ToastMessageProps } from "./ToastMessageProps";

import "./ToastMessage.scss";

/**
 * ToastMessage — short, non-blocking notification pill used to surface
 * transient messages anywhere in the application.
 *
 * Renders nothing when there is no message. Visibility/animation state is
 * driven by the `isVisible` prop; the auto-hide timer lives in
 * `ToastMessageContainer`, not in this presentational component.
 */
const ToastMessage: FunctionComponent<ToastMessageProps> = ({
  message,
  isVisible,
  className: customClassName
}: ToastMessageProps): JSX.Element | null => {
  if (!message) {
    return null;
  }

  const isBrowser: boolean = DeviceModel.isDesktopDevice();

  const baseClassName: string =
    "toast-message" +
    (isBrowser ? " toast-message--browser" : "") +
    (isVisible ? " toast-message--visible" : " toast-message--hidden");

  const className: string = customClassName
    ? baseClassName + " " + customClassName
    : baseClassName;

  return (
    <div className={className} data-testid="toast-message" aria-live="polite">
      <span className="toast-message__text">{message}</span>
    </div>
  );
};

export default ToastMessage;
